import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { formatCurrency } from "../../utils/formatters";

const LocationsTab = ({ locations, isMobile, onUpdateLocationCost }) => {
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [editingLocationCost, setEditingLocationCost] = useState("");
  const [editingLocationMonthlyCost, setEditingLocationMonthlyCost] = useState("");

  const handleStartEdit = (location) => {
    setEditingLocationId(location.id);
    setEditingLocationCost(location.hourly_cost?.toString() || "");
    setEditingLocationMonthlyCost(location.monthly_cost?.toString() || "");
  };

  const handleCancelEdit = () => {
    setEditingLocationId(null);
    setEditingLocationCost("");
    setEditingLocationMonthlyCost("");
  };

  const handleSaveEdit = async (locationId) => {
    const success = await onUpdateLocationCost(
      locationId,
      editingLocationCost,
      editingLocationMonthlyCost
    );
    if (success) {
      setEditingLocationId(null);
      setEditingLocationCost("");
      setEditingLocationMonthlyCost("");
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, color: "text.secondary" }}>
        Ustaw koszt godzinowy wynajmu oraz — opcjonalnie — stałą kwotę
        miesięczną dla każdej lokalizacji. Stała kwota doliczana jest za każdy
        miesiąc w wybranym zakresie, niezależnie od liczby zajęć.
      </Typography>
      <TableContainer>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell>Lokalizacja</TableCell>
              <TableCell>Adres</TableCell>
              <TableCell align="right">Koszt godzinowy</TableCell>
              <TableCell align="right">Stała kwota miesięczna</TableCell>
              <TableCell align="center" sx={{ width: 100 }}>
                Akcja
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.map((location) => (
              <TableRow key={location.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {location.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {location.address}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {editingLocationId === location.id ? (
                    <TextField
                      value={editingLocationCost}
                      onChange={(e) => setEditingLocationCost(e.target.value)}
                      type="number"
                      size="small"
                      sx={{ width: 120 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">PLN</InputAdornment>
                        ),
                      }}
                      autoFocus
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: location.hourly_cost
                          ? "success.main"
                          : "warning.main",
                      }}
                    >
                      {location.hourly_cost !== null
                        ? formatCurrency(location.hourly_cost)
                        : "Nie ustawiono"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {editingLocationId === location.id ? (
                    <TextField
                      value={editingLocationMonthlyCost}
                      onChange={(e) =>
                        setEditingLocationMonthlyCost(e.target.value)
                      }
                      type="number"
                      size="small"
                      sx={{ width: 120 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">PLN</InputAdornment>
                        ),
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: location.monthly_cost
                          ? "success.main"
                          : "text.secondary",
                      }}
                    >
                      {location.monthly_cost
                        ? `${formatCurrency(location.monthly_cost)} / mies.`
                        : "Brak"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {editingLocationId === location.id ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleSaveEdit(location.id)}
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={handleCancelEdit}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => handleStartEdit(location)}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {locations.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Brak lokalizacji
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LocationsTab;
